import React, { useEffect, useState } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CButton,
  CBadge,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSun, cilHome, cilSettings, cilCloud, cilCheckCircle, cilWarning } from '@coreui/icons';
import { smartThingsService } from '../../../services/smartThingsService';
import HomeAssistantDevices from './HomeAssistantDevices';
import HomeAssistantAutomations from './HomeAssistantAutomations';
import HomeAssistantIntegrations from './HomeAssistantIntegrations';

const SECTION_KEYS = {
  OVERVIEW: 'overview',
  DEVICES: 'devices',
  AUTOMATIONS: 'automations',
  INTEGRATIONS: 'integrations',
};

const HomeAssistant = ({ authUser, initialTab = SECTION_KEYS.OVERVIEW }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [googleDeviceCount, setGoogleDeviceCount] = useState(0);
  const [smartThingsDevices, setSmartThingsDevices] = useState([]);
  const [quickActionState, setQuickActionState] = useState('Idle');

  const hasSmartThings = Boolean(localStorage.getItem('smartThingsToken'));
  const hasGoogle = Boolean(localStorage.getItem('googleAccessToken'));
  const hasHomeAssistant = Boolean(localStorage.getItem('ha_token'));

  useEffect(() => {
    setActiveTab(initialTab || SECTION_KEYS.OVERVIEW);
  }, [initialTab]);

  useEffect(() => {
    const fetchGoogleDevices = async () => {
      try {
        const googleToken = localStorage.getItem('googleAccessToken');
        if (!googleToken) return;

        const response = await fetch(
          'https://smartdevicemanagement.googleapis.com/v1/enterprises/ed20431e-7935-4274-96a2-7be6fec49ee8/devices',
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${googleToken}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setGoogleDeviceCount(Array.isArray(data.devices) ? data.devices.length : 0);
        }
      } catch (error) {
        console.error('Error fetching Google devices:', error);
      }
    };

    const fetchSmartThingsDevices = async () => {
      try {
        const token = localStorage.getItem('smartThingsToken');
        if (!token) return;
        const response = await smartThingsService.getDevices(token);
        setSmartThingsDevices(response.items || []);
      } catch (error) {
        console.error('Error fetching SmartThings devices:', error);
      }
    };

    fetchGoogleDevices();
    fetchSmartThingsDevices();
  }, []);

  const runQuickAction = (actionLabel) => {
    setQuickActionState(`Executed: ${actionLabel}`);
  };

  const stats = [
    { label: 'Total Connected Devices', value: smartThingsDevices.length + googleDeviceCount },
    { label: 'SmartThings Devices', value: smartThingsDevices.length },
    { label: 'Google Home Devices', value: googleDeviceCount },
    { label: 'Automations Ready', value: hasSmartThings ? 'Live + Demo' : 'Demo Only' },
  ];

  return (
    <div className="animated fadeIn">
      <CRow>
        <CCol>
          <h1 className="mb-2">IoT Hub</h1>
          <p className="text-medium-emphasis mb-4">
            Consolidated workspace for overview, device control, automations, and integrations.
          </p>
          <CNav variant="tabs" className="mb-4">
            <CNavItem>
              <CNavLink active={activeTab === SECTION_KEYS.OVERVIEW} onClick={() => setActiveTab(SECTION_KEYS.OVERVIEW)}>
                Overview
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink active={activeTab === SECTION_KEYS.DEVICES} onClick={() => setActiveTab(SECTION_KEYS.DEVICES)}>
                Devices
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink active={activeTab === SECTION_KEYS.AUTOMATIONS} onClick={() => setActiveTab(SECTION_KEYS.AUTOMATIONS)}>
                Automations
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink active={activeTab === SECTION_KEYS.INTEGRATIONS} onClick={() => setActiveTab(SECTION_KEYS.INTEGRATIONS)}>
                Integrations
              </CNavLink>
            </CNavItem>
          </CNav>
        </CCol>
      </CRow>

      <CTabContent>
        <CTabPane visible={activeTab === SECTION_KEYS.OVERVIEW}>
          <CRow>
            <CCol md={6} lg={3}>
              <CCard className="mb-4">
                <CCardHeader>
                  <CIcon icon={cilSun} className="me-2" />
                  Quick Actions
                </CCardHeader>
                <CCardBody>
                  <div className="d-grid gap-2">
                    <CButton color="primary" onClick={() => runQuickAction('All Lights Off')}>
                      All Lights Off
                    </CButton>
                    <CButton color="info" onClick={() => runQuickAction('Away Mode')}>
                      Away Mode
                    </CButton>
                    <CButton color="success" onClick={() => runQuickAction('Movie Mode')}>
                      Movie Mode
                    </CButton>
                  </div>
                  <small className="d-block mt-3 text-medium-emphasis">{quickActionState}</small>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol md={6} lg={3}>
              <CCard className="mb-4">
                <CCardHeader>
                  <CIcon icon={cilHome} className="me-2" />
                  Stats
                </CCardHeader>
                <CCardBody>
                  {stats.map((stat) => (
                    <div key={stat.label} className="d-flex justify-content-between align-items-center mb-3">
                      <span>{stat.label}</span>
                      <strong>{stat.value}</strong>
                    </div>
                  ))}
                </CCardBody>
              </CCard>
            </CCol>
            <CCol md={6} lg={3}>
              <CCard className="mb-4">
                <CCardHeader>
                  <CIcon icon={cilSettings} className="me-2" />
                  Integration Status
                </CCardHeader>
                <CCardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span>SmartThings</span>
                    <CBadge color={hasSmartThings ? 'success' : 'secondary'}>{hasSmartThings ? 'Connected' : 'Not Connected'}</CBadge>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span>Home Assistant</span>
                    <CBadge color={hasHomeAssistant ? 'success' : 'secondary'}>{hasHomeAssistant ? 'Connected' : 'Not Connected'}</CBadge>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span>Google Home</span>
                    <CBadge color={hasGoogle ? 'success' : 'secondary'}>{hasGoogle ? 'Connected' : 'Not Connected'}</CBadge>
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol md={6} lg={3}>
              <CCard className="mb-4">
                <CCardHeader>
                  <CIcon icon={cilCloud} className="me-2" />
                  Platforms
                </CCardHeader>
                <CCardBody>
                  {[
                    { name: 'SmartThings', connected: hasSmartThings },
                    { name: 'Home Assistant', connected: hasHomeAssistant },
                    { name: 'Google Home', connected: hasGoogle },
                    { name: 'Matter', connected: false },
                  ].map((platform) => (
                    <div key={platform.name} className="d-flex justify-content-between align-items-center mb-3">
                      <span>{platform.name}</span>
                      <CIcon icon={platform.connected ? cilCheckCircle : cilWarning} />
                    </div>
                  ))}
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CTabPane>

        <CTabPane visible={activeTab === SECTION_KEYS.DEVICES}>
          <HomeAssistantDevices />
        </CTabPane>

        <CTabPane visible={activeTab === SECTION_KEYS.AUTOMATIONS}>
          <HomeAssistantAutomations />
        </CTabPane>

        <CTabPane visible={activeTab === SECTION_KEYS.INTEGRATIONS}>
          <HomeAssistantIntegrations authUser={authUser} />
        </CTabPane>
      </CTabContent>
    </div>
  );
};

export default HomeAssistant;
